import { useEffect, useState, useCallback, useMemo } from "react";
import {
    Search,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Monitor,
    ChevronUp,
    ChevronDown,
    Loader2,
    AlertTriangle,
    CheckCircle,
    Power,
    Wrench, // Added icon for the Action button
    ListChecks,
    Check
} from "lucide-react"; // Adjust path as needed
import { showError } from "../../utils/toast";
import baseapi from "../../services/baseapi";
import { useClientLevel } from "../../hooks/useClientLevel";
import { CustomDropdown } from "../../components/CustomDropdown ";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";
import OSPatchableAssetsPanel from "./OSPatchableAssetsPanel";
import { useNavigate } from "react-router-dom";
import ColumnVisibilityDropdown, { type ColumnOption } from "../../components/ColumnVisibilityDropdown";

export interface OSUpdate {
    name: string;
    title: string;
    new_version: string | null;
    severity: string;
    reboot_required: boolean;
    os_type: string;
    pkg_manager: string;
    kb_ids: string | any[];
    cve_ids: string | any[];
    categories: string | any[];
    affected_asset_count: number;
    recommended: boolean;
}

interface ApiResponse {
    status: boolean;
    message: string;
    data: OSUpdate[];
    total?: number;
}

type SortKey =
    | "title"
    | "severity"
    | "os_type"
    | "pkg_manager"
    | "reboot_required"
    | "affected_asset_count"
    | "new_version"
    | "recommended"; // Added new_version as a sort key

export type OSUpdateColumnKey =
    | "title"
    | "new_version"
    | "severity"
    | "os_type"
    | "pkg_manager"
    | "reboot_required"
    | "recommended"
    | "affected_asset_count"
    | "actions";

export default function PatchableOSTable() {
    const [updates, setUpdates] = useState<OSUpdate[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Record<OSUpdateColumnKey, boolean>>({
        title: true,
        new_version: true,
        severity: true,
        os_type: true,
        pkg_manager: true,
        reboot_required: true,
        recommended: true,
        affected_asset_count: true,
        actions: true,
    });

    const columnOptions: ColumnOption<OSUpdateColumnKey>[] = useMemo(() => [
        { key: "title", label: "Update Title" },
        { key: "new_version", label: "New Version" },
        { key: "severity", label: "Severity" },
        { key: "os_type", label: "OS Type" },
        { key: "pkg_manager", label: "Pkg Manager" },
        { key: "reboot_required", label: "Reboot" },
        { key: "recommended", label: "Recommended" },
        { key: "affected_asset_count", label: "Assets" },
        { key: "actions", label: "Action" },
    ], []);

    const visibleColsCount = Object.values(visibleColumns).filter(Boolean).length || 1;

    const level = useClientLevel();
    const client_id = level?.id;

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const [sortKey, setSortKey] = useState<SortKey>("affected_asset_count");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

    // Filters
    const [osFilter, setOsFilter] = useState<string>("");
    const [rebootFilter, setRebootFilter] = useState<string>("");

    const [selectedUpdate, setSelectedUpdate] = useState<any>(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    const navigate = useNavigate();


    const osOptions = [
        { label: "All OS", value: "" },
        { label: "Windows", value: "windows" },
        { label: "Linux", value: "linux" },
    ];

    const rebootOptions = [
        { label: "All", value: "" },
        { label: "Required", value: "true" },
        { label: "Not Required", value: "false" },
    ];

    // Debounce Search
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // API Call
    const fetchUpdates = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};

            // Apply Filters to query
            if (osFilter) queryObj.os_type = osFilter;
            if (rebootFilter !== "") queryObj.reboot_required = rebootFilter === "true";

            if (debouncedSearch) {
                queryObj.$or = [
                    { title: { $ilike: `%${debouncedSearch}%` } },
                    { name: { $ilike: `%${debouncedSearch}%` } }
                ];
            }

            const params: any = {
                client_id,
                skip: (currentPage - 1) * limit,
                limit: limit,
                search: debouncedSearch || undefined,
                sort_by: sortKey,
                sort_dir: sortDir,
                os_type: osFilter || undefined,
                reboot_required: rebootFilter !== "" ? rebootFilter : undefined,
                query: JSON.stringify(queryObj)
            };

            const res = await baseapi.get<ApiResponse>("/patch/os/pending_updates", { params });

            if (res.data.status) {
                setUpdates(res.data.data ?? []);
                setTotalRecords(res.data.total ?? res.data.data?.length ?? 0);
            } else {
                showError(res.data.message || "Failed to load OS updates");
            }
        } catch (e: any) {
            showError(e.message);
        } finally {
            setLoading(false);
        }
    }, [client_id, debouncedSearch, currentPage, limit, sortKey, sortDir, osFilter, rebootFilter]);

    useEffect(() => {
        fetchUpdates();
    }, [fetchUpdates]);

    // Pagination Calculations
    const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
        setCurrentPage(1);
    };

    const handlePatch = (update: OSUpdate) => {
        setSelectedUpdate(update);
        setIsPanelOpen(true);
    };

    const getSeverityStyle = (severity: string) => {
        const s = severity?.toLowerCase() || "";
        if (s === "critical") return "bg-red-500/10 text-red-400 border-red-500/30";
        if (s === "important") return "bg-orange-500/10 text-orange-400 border-orange-500/30";
        if (s === "low") return "bg-blue-500/10 text-blue-400 border-blue-500/30";
        return "bg-gray-800 text-gray-400 border-gray-700";
    };

    const SortHeader = ({ label, keyName }: { label: string; keyName: SortKey }) => {
        const active = sortKey === keyName;
        const Icon = sortDir === "asc" ? ChevronUp : ChevronDown;
        return (
            <th
                onClick={() => handleSort(keyName)}
                className="px-4 py-2 text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap cursor-pointer select-none group"
            >
                <div className="flex items-center gap-1 group-hover:text-orange-300 transition-colors">
                    {label}
                    {active ? (
                        <Icon className="w-3 h-3 text-orange-400" />
                    ) : (
                        <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-40" />
                    )}
                </div>
            </th>
        );
    };

    const getOsIcon = (os?: string) => {
        if (!os) return null;
        const value = os.toLowerCase();

        if (value.includes("windows")) return "/windows.png";
        if (value.includes("mac") || value.includes("darwin") || value.includes("os x")) return "/mac.png";
        if (value.includes("linux") || value.includes("ubuntu") || value.includes("debian") || value.includes("centos"))
            return "/linux.svg";

        return null;
    };

    return (
        <div className="flex flex-col bg-transparent text-gray-200">
            {/* Header / Toolbar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-1 flex-wrap">
                <div className="flex items-center gap-2">
                    <div className="bg-orange-600 p-1.5 rounded border border-orange-500/30 shadow-lg">
                        <Monitor size={16} className="text-white" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <h1 className="text-lg font-bold text-white tracking-wide">
                            Pending OS Updates
                        </h1>
                        {totalRecords > 0 && (
                            <span className="text-white font-bold">
                                ({totalRecords})
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-3 ml-auto">
                    {/* OS Type Dropdown */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">OS:</span>
                        <CustomDropdown
                            data={osOptions}
                            selectedValue={osFilter}
                            onSelect={(val) => { setOsFilter(val); setCurrentPage(1); }}
                            placeholder="All OS"
                            width="w-32"
                            buttonHeight={8}
                            className="text-[8px]"
                        />
                    </div>

                    {/* Reboot Required Dropdown */}
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Reboot:</span>
                        <CustomDropdown
                            data={rebootOptions}
                            selectedValue={rebootFilter}
                            onSelect={(val) => { setRebootFilter(val); setCurrentPage(1); }}
                            placeholder="All"
                            width="w-40"
                            buttonHeight={8}
                            className="text-[8px]"
                        />
                    </div>
                    <button
                        onClick={() => navigate(`/patching/jobs?prev=os&tab=os`)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 rounded-lg text-gray-300 hover:text-white text-[11px] font-bold uppercase tracking-widest transition-all shadow-sm group"
                    >
                        <ListChecks size={14} className="text-gray-400 group-hover:text-orange-400 transition-colors" />
                        Jobs
                    </button>

                    <button
                        onClick={fetchUpdates}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg h-8 w-8 flex items-center justify-center text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0"
                    >
                        <RefreshCw
                            size={14}
                            className={loading ? "animate-spin text-orange-400" : ""}
                        />
                    </button>
                    {/* Search */}
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200 group-focus-within:text-orange-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search KB, Title..."
                            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] h-8 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 transition-all"
                        />
                    </div>

                    <ColumnVisibilityDropdown
                        columns={columnOptions}
                        visibleColumns={visibleColumns}
                        onChange={setVisibleColumns}
                    />
                </div>
            </div>

            {/* Table container */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl overflow-hidden shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-700/50">
                                {visibleColumns.title && (
                                    <SortHeader label="Update Title" keyName="title" />
                                )}
                                {visibleColumns.new_version && (
                                    <SortHeader label="New Version" keyName="new_version" />
                                )}
                                {visibleColumns.severity && (
                                    <SortHeader label="Severity" keyName="severity" />
                                )}
                                {visibleColumns.os_type && (
                                    <SortHeader label="OS Type" keyName="os_type" />
                                )}
                                {visibleColumns.pkg_manager && (
                                    <SortHeader label="Pkg Manager" keyName="pkg_manager" />
                                )}
                                {visibleColumns.reboot_required && (
                                    <SortHeader label="Reboot" keyName="reboot_required" />
                                )}
                                {visibleColumns.recommended && (
                                    <SortHeader label="Recommended" keyName="recommended" />
                                )}
                                {visibleColumns.affected_asset_count && (
                                    <SortHeader label="Assets" keyName="affected_asset_count" />
                                )}
                                {visibleColumns.actions && (
                                    <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-100 uppercase tracking-widest whitespace-nowrap select-none">
                                        Action
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-gray-200">
                                            <Loader2 className="w-4 h-4 animate-spin text-orange-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest">
                                                Loading OS updates...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : updates.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-40">
                                            <Monitor size={24} className="text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-200">
                                                No pending OS updates found
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                updates.map((update, idx) => (
                                    <tr key={`${update.name}-${idx}`} className="hover:bg-gray-700/50 transition-colors">
                                        {/* Title & Name */}
                                        {visibleColumns.title && (
                                            <td className="px-4 py-2.5 whitespace-normal min-w-[300px]">
                                                <div className="flex flex-col gap-0.5">
                                                    <span className="text-[11px] font-bold text-gray-100 line-clamp-2" title={update.title}>
                                                        {update.title}
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono text-[9px] text-gray-400 bg-gray-900 px-1.5 py-0.5 rounded border border-gray-800">
                                                            {update.name}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                        )}

                                        {/* New Version (Moved to its own column) */}
                                        {visibleColumns.new_version && (
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {update.new_version ? (
                                                    <span className="font-mono text-[10px] text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded">
                                                        v{update.new_version}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-500 font-bold">-</span>
                                                )}
                                            </td>
                                        )}

                                        {/* Severity */}
                                        {visibleColumns.severity && (
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${getSeverityStyle(update.severity)}`}>
                                                    {update.severity?.toLowerCase() === 'critical' ? <AlertTriangle size={10} /> : null}
                                                    {update.severity || "Unknown"}
                                                </span>
                                            </td>
                                        )}

                                        {/* OS Type */}
                                        {visibleColumns.os_type && (
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <div className="text-[10px] flex items-center gap-1 font-black text-gray-300 uppercase tracking-wider">
                                                    {getOsIcon(update.os_type) && (
                                                        <img
                                                            src={getOsIcon(update.os_type)!}
                                                            alt="os"
                                                            className="w-3.5 h-3.5 flex-shrink-0"
                                                        />
                                                    )}
                                                    {update.os_type}
                                                </div>
                                            </td>
                                        )}

                                        {/* Pkg Manager */}
                                        {visibleColumns.pkg_manager && (
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="text-[9px] text-gray-200 font-mono uppercase bg-gray-800 px-1.5 py-0.5 rounded border border-gray-700">
                                                    {update.pkg_manager}
                                                </span>
                                            </td>
                                        )}

                                        {/* Reboot Required */}
                                        {visibleColumns.reboot_required && (
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {update.reboot_required ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-400">
                                                        <Power size={12} className="text-orange-500" />
                                                        Yes
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-200">
                                                        <CheckCircle size={12} />
                                                        No
                                                    </span>
                                                )}
                                            </td>
                                        )}

                                        {/* Recommended */}
                                        {visibleColumns.recommended && (
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {update.recommended ? (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-400">
                                                        <Check size={12} className="text-orange-500" />
                                                        Yes
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-gray-200">
                                                        <CheckCircle size={12} />
                                                        No
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        
                                        {/* Affected Assets */}
                                        {visibleColumns.affected_asset_count && (
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                                                    {update.affected_asset_count}
                                                </span>
                                            </td>
                                        )}

                                        {/* Action / Patch Button */}
                                        {visibleColumns.actions && (
                                            <td className="px-4 py-2.5 whitespace-nowrap text-right">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handlePatch(update);
                                                    }}
                                                    className="inline-flex cursor-pointer items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:border-orange-500/60 rounded text-[9px] font-bold uppercase tracking-wider transition-all"
                                                >
                                                    <Wrench size={12} />
                                                    Patch
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => {
                                setLimit(v);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[9px] font-medium text-gray-200 tracking-widest">
                            <span className="text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-gray-600">/</span>
                            <span className="text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {(debouncedSearch || osFilter || rebootFilter) && (
                            <button
                                onClick={() => {
                                    setSearchTerm("");
                                    setOsFilter("");
                                    setRebootFilter("");
                                }}
                                className="text-[9px] text-orange-400 hover:text-orange-300 font-bold uppercase tracking-widest transition-colors"
                            >
                                Clear filters
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            disabled={currentPage === 1 || loading}
                            onClick={() => setCurrentPage(1)}
                            className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                        >
                            <ChevronsLeft size={14} />
                        </button>
                        <button
                            disabled={currentPage === 1 || loading}
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                        >
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                            {currentPage}
                        </div>
                        <button
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                        >
                            <ChevronRight size={14} />
                        </button>
                        <button
                            disabled={currentPage >= totalPages || loading}
                            onClick={() => setCurrentPage(totalPages)}
                            className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30"
                        >
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>

            <OSPatchableAssetsPanel
                updateName={selectedUpdate?.name}
                updateTitle={selectedUpdate?.title}
                clientId={client_id}
                isOpen={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                search={debouncedSearch}
            />
        </div>
    );
}